import { PATHS } from '@/shared/config';

import {
  TEMPLATE_EDIT_SEARCH_PARAM,
  TEMPLATE_SERIES_SEARCH_PARAM,
} from '../config';

export type TemplatesNavigationState = {
  templatesBackPath: string;
  // Set when the page at templatesBackPath pushed this history entry, so Back
  // can return by going back in history.
  pushedFromBackPath?: boolean;
};

export const DEFAULT_TEMPLATES_BACK_PATH = '/company-settings?tab=Templates';

export const getPathWithSearch = (pathname: string, search = ''): string =>
  search ? `${pathname}${search}` : pathname;

export const getTemplatesPagePath = (
  seriesId?: string,
  { edit = false }: { edit?: boolean } = {},
): string =>
  getPathWithSearch(
    PATHS.transcriptorTemplates,
    seriesId
      ? `?${new URLSearchParams({
          [TEMPLATE_SERIES_SEARCH_PARAM]: seriesId,
          ...(edit && { [TEMPLATE_EDIT_SEARCH_PARAM]: '1' }),
        })}`
      : '',
  );

// A document's "Edit template" opens the series in the edit mode in a tab of
// its own, so the recording or note it was opened from stays where it is.
export const openTemplateEditor = (seriesId?: string) =>
  window.open(
    getTemplatesPagePath(seriesId, { edit: true }),
    '_blank',
    'noopener',
  );

export const createTemplatesNavigationState = (
  templatesBackPath: string,
): TemplatesNavigationState => ({
  templatesBackPath,
});

export const hasTemplatesNavigationState = (
  state: unknown,
): state is TemplatesNavigationState =>
  Boolean(
    state &&
      typeof state === 'object' &&
      'templatesBackPath' in state &&
      typeof state.templatesBackPath === 'string' &&
      state.templatesBackPath,
  );

export const resolveTemplatesBackPath = (
  state: unknown,
  fallback = DEFAULT_TEMPLATES_BACK_PATH,
): string => {
  if (hasTemplatesNavigationState(state)) {
    return state.templatesBackPath;
  }

  return fallback;
};

export const parseTemplatesPath = (path: string) => {
  const [pathnamePart, searchPart] = path.split('?');
  const pathname = pathnamePart || DEFAULT_TEMPLATES_BACK_PATH;
  const search = searchPart ? `?${searchPart}` : '';

  return {
    pathname,
    search,
    searchParams: new URLSearchParams(searchPart ?? ''),
  };
};
